/**
 * CrmEntityLink Component
 *
 * Displays a clickable link to a CRM entity (Deal, Lead, Contact, Company).
 * Opens the CRM entity detail page in a new tab.
 */

import { memo } from 'react';
import { ExternalLink, Briefcase, Target, User, Building } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  buildCrmEntityUrl,
  formatCrmEntity,
  type CrmEntityType,
} from '@/lib/utils/crm-links';

export interface CrmEntityLinkProps {
  entityType: CrmEntityType;
  entityId: string;
  entityName?: string | null;
  domain?: string | null;
  /**
   * Which CRM the entity lives in — the record's own `crmVendor`, never the
   * org's. There is deliberately NO default: `vendor = 'bitrix24'` used to sit
   * here, and in an org connected to both CRMs it turned a Uspacy entity id
   * into a Bitrix24 URL that resolves to a real card belonging to someone
   * else. Absent vendor or absent domain → plain text.
   */
  vendor?: string | null;
  showIcon?: boolean;
  className?: string;
  'data-testid'?: string;
}

/**
 * Get icon component for CRM entity type
 */
function getEntityIcon(entityType: CrmEntityType) {
  switch (entityType) {
    case 'deal':
      return Briefcase;
    case 'lead':
      return Target;
    case 'contact':
      return User;
    case 'company':
      return Building;
    default:
      return ExternalLink;
  }
}

export const CrmEntityLink = memo(function CrmEntityLink({
  entityType,
  entityId,
  entityName,
  domain,
  vendor,
  showIcon = true,
  className,
  'data-testid': testId = 'crm-entity-link',
}: CrmEntityLinkProps) {
  const displayText = formatCrmEntity(entityType, entityId, entityName);
  const Icon = getEntityIcon(entityType);

  // A link only when we can build a real one. `buildCrmEntityUrl` returns null
  // for a vendor whose URL shape we do not know, and rendering `href={null}`
  // would produce an anchor that navigates to the current page — worse than the
  // plain-text fallback below, which at least reads as "no link available".
  const url = buildCrmEntityUrl(vendor, domain, entityType, entityId);
  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel={['noopener', 'noreferrer'].join(' ')}
        className={cn(
          'inline-flex items-center gap-1.5 text-primary hover:text-primary',
          'transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none',
          'rounded-sm',
          className
        )}
        data-testid={testId}
        title={displayText}
      >
        {showIcon && (
          <Icon
            className="h-4 w-4 flex-shrink-0"
            data-testid={`${testId}-icon`}
            aria-hidden="true"
          />
        )}
        <span className="truncate" data-testid={`${testId}-text`}>
          {displayText}
        </span>
        <ExternalLink
          className="h-3 w-3 flex-shrink-0 text-fg-muted"
          aria-hidden="true"
        />
      </a>
    );
  }

  // If no domain, render as plain text (no link)
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 text-fg-muted', className)}
      data-testid={testId}
    >
      {showIcon && (
        <Icon
          className="h-4 w-4 flex-shrink-0"
          data-testid={`${testId}-icon`}
          aria-hidden="true"
        />
      )}
      <span className="truncate" data-testid={`${testId}-text`}>
        {displayText}
      </span>
    </span>
  );
});

export default CrmEntityLink;
